// ABOUTME: The /interview page behind the email link: redeems `?t=`, then shows the card, or the screen that says why
// ABOUTME: it cannot (link unknown or expired, interviews off, not a talent's link).
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import { LoadingScreen, NotFoundErrorScreen } from 'components/screens';
import { ErrorScreen } from 'components/screens/parts/ErrorScreen';
import { getTrans, TranslationKey } from 'modules/i18n';
import { Layout } from 'modules/minimal-ui-theme';
import { STILL_ON_ANSWERS, StillOnAnswer } from '../constants';
import {
  GuestInterviewState,
  useGuestInterviewSession
} from '../useGuestInterviewSession';
import { InterviewCard } from './InterviewCard';

type DeadEnd = Exclude<
  GuestInterviewState,
  'loading' | 'active' | 'session-expired'
>;

const DEAD_END_COPY: Record<
  DeadEnd,
  { title: TranslationKey; body: TranslationKey }
> = {
  'missing-token': {
    title: 'interviews.guest.invalid.title',
    body: 'interviews.guest.invalid.body'
  },
  'not-found': {
    title: 'interviews.guest.invalid.title',
    body: 'interviews.error.notFound'
  },
  unavailable: {
    title: 'interviews.guest.unavailable.title',
    body: 'interviews.error.unavailable'
  },
  'wrong-role': {
    title: 'interviews.guest.invalid.title',
    body: 'interviews.guest.wrongRole'
  },
  error: {
    title: 'interviews.guest.error.title',
    body: 'interviews.error.unknown'
  }
};

const answerOf = (value: string | null): StillOnAnswer | null =>
  STILL_ON_ANSWERS.find(answer => answer === value) ?? null;

export const GuestInterviewFlow = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const linkToken = searchParams.get('t');
  const initialAnswer = answerOf(searchParams.get('answer'));
  const { state, interviewRefId, api, reload } =
    useGuestInterviewSession(linkToken);

  // The answer is acted on once; a refresh must not reopen its dialog. `t` stays: a refresh re-redeems from it.
  const dropAnswer = useCallback(
    () =>
      setSearchParams(
        current => {
          const next = new URLSearchParams(current);
          next.delete('answer');
          return next;
        },
        { replace: true }
      ),
    [setSearchParams]
  );

  if (state === 'loading') return <LoadingScreen />;

  // The link expired or a newer email replaced it: say so, and offer one more redeem from the link in hand.
  if (state === 'session-expired') {
    return (
      <ErrorScreen
        title={getTrans('interviews.guest.sessionExpired.title')}
        description1={getTrans('interviews.guest.sessionExpired.body')}
        description2=""
        buttonText={getTrans('interviews.guest.sessionExpired.reload')}
        onButtonClick={reload}
      />
    );
  }

  if (state !== 'active' || !api || !interviewRefId) {
    const copy = DEAD_END_COPY[state === 'active' ? 'error' : state];
    return (
      <NotFoundErrorScreen
        title={getTrans(copy.title)}
        description1={getTrans(copy.body)}
        description2=""
      />
    );
  }

  return (
    <Layout>
      <Box sx={{ maxWidth: 560, mx: 'auto' }}>
        <InterviewCard
          api={api}
          refId={interviewRefId}
          initialAnswer={initialAnswer}
          onInitialAnswerHandled={dropAnswer}
        />
      </Box>
    </Layout>
  );
};
